// `sessionStates: Record<string, SessionState>` types indexed access as
// always-defined even though a session that hasn't been initialized yet is
// genuinely absent at runtime. `use-chat.ts`'s `readSessionState` disables
// this same rule file-wide for the identical reason — match that precedent
// rather than drop the defensive `?.`/`??` fallbacks below.
/* eslint-disable @typescript-eslint/no-unnecessary-condition, max-lines */
import { useEffect, useRef } from "react"

import { focusManager } from "@tanstack/react-query"

import type { ChatStore } from "./chat-request-registry"
import type {
  ChatSessionModel,
  DurableChatTurnCurrentResponse,
  DurableChatTurnIdentity,
  DurableChatTurnSnapshot,
} from "@/api/models"
import type { SessionState } from "@/stores/slices/chat-slice"
import type { QueryClient } from "@tanstack/react-query"

import { getCurrentDurableChatTurnApi } from "@/api/api"
import { ApiError } from "@/api/client/_shared"
import { isUnknownApiRouteError } from "@/features/chat/utils/durable-chat-turn-mode"
import { settleSteerNotesAtTurnEnd } from "@/features/chat/utils/steer-note-settlement"
import { routeFairUseError, routeFairUseMetadata } from "@/features/fair-use/fair-use-ws-bridge"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"

import { durableChatTurnRegistry } from "./durable-chat-turn-registry"
import { computeBackoffDelayMs, isTerminalStatus } from "./durable-chat-turn-registry-utils"
import {
  applyDurableSessionEnrichmentToSessions,
  mapDurableChatTurnStateToSessionPatch,
} from "./durable-chat-turn-state"
import {
  applyDurableSessionPlaceholders,
  registerDurableSessionPlaceholder,
  releaseDurableSessionPlaceholder,
} from "./durable-session-placeholders"
import {
  activitySideEffectKey,
  applyActivitySideEffects,
} from "./use-websocket-message-handler-utils"

/**
 * Register a durable turn (freshly accepted, or discovered on hydration)
 * with the module-scoped `durableChatTurnRegistry` and mirror every
 * registry update into the session's chat state via the same status → UI
 * mapping used everywhere else. Returns an unsubscribe function.
 *
 * This is the single write path for durable-turn UI state: both a fresh
 * `sendChatRequest` accept and a hydration read funnel through it, so a
 * page reload can never diverge from what a live tab would render, and can
 * never re-run `mapDurableChatTurnStateToSessionPatch` differently.
 *
 * Detaching the returned unsubscribe (route change, unmount, session
 * switch) only stops this UI mirror — per the registry's own contract,
 * "detaching the last listener must never stop the turn subscription or
 * cancel server-owned execution." Nothing here ever calls the cancel API.
 */
export function attachDurableChatTurnToSession({
  authoritative = false,
  chatStore,
  isMessageQueueEnabled = false,
  getToken,
  identity,
  onSessionListSettled,
  placeholderTitle,
  projectId,
  question,
  queryClient,
  sessionType,
  snapshot,
}: {
  /** True only when `snapshot` is a genuine covering snapshot document —
   *  see `DurableChatTurnRegistryRegistration.authoritative`'s doc comment
   *  for the full contract. Defaults to `false`: neither caller of this
   *  function (a fresh accept response, or a `/turns/current` hydration
   *  read) carries one today. Explicit pass-through (rather than always
   *  hardcoding `false` here) so a caller that DOES have the real
   *  `replayGap` signal can honestly report it instead of every call site
   *  re-deriving the same "always false" literal (issue #2513 finding 2). */
  authoritative?: boolean
  chatStore: ChatStore
  /** Whether `explorer_message_queue` is on for this user. Decides what happens
   *  to a steer note the model never saw when the turn ends (#2469): requeue it
   *  as an ordinary message, or keep it as a dismissable `undelivered` chip. */
  isMessageQueueEnabled?: boolean
  getToken: () => string | undefined
  identity: DurableChatTurnIdentity
  /**
   * Called EXACTLY ONCE per attach, in the same one-way terminal branch as
   * the authoritative session-list invalidate below and immediately after
   * it. Exists for cross-window sync (issue #2548): a durable session is
   * named only at completion (`ChatTurnRunner._complete` →
   * `_enrich_and_publish_session`), so the moment this fires is the moment
   * the OTHER windows' sidebars have something new to show and need a hint
   * to go and refetch it.
   *
   * Deliberately terminal-only, never per emit: `writePatch` runs for every
   * frame of a live turn, and a broadcast there would put the hint bus on
   * the streaming hot path — which issue #2548 explicitly rules out.
   *
   * Optional, and never called when omitted, so the many direct callers in
   * tests that only exercise the enrichment/patch mirror stay unaffected.
   */
  onSessionListSettled?: () => void
  /**
   * Sidebar placeholder title (the translated `chatsession.new_chat`, i.e.
   * "New chat") to cover this session with for as long as the turn is
   * NON-TERMINAL — see `durable-session-placeholders.ts`.
   *
   * Registered here, not only on the send path, because the send path is not
   * the only way a browser ends up watching an untitled in-flight durable
   * chat: reloading mid-turn, or opening the chat in a second tab, throws
   * away every optimistic cache write and re-enters through
   * `useDurableChatTurnHydration` → this function. Without a placeholder that
   * viewer's session list comes back from the server still untitled
   * (`title: ""`, since enrichment only names the chat at completion) and
   * `use-chat-history.ts`'s `sessions.filter((s) => s?.title)` hides the chat
   * for the remainder of the turn — i.e. exactly the state a user who
   * refreshes lands in.
   *
   * Optional so the many direct callers in tests that only exercise the
   * enrichment/patch mirror stay unaffected: omitting it registers nothing
   * and changes nothing. Both production call sites (the durable send in
   * `use-chat.ts` and `useDurableChatTurnHydration`) pass it, and
   * `use-chat.durable-turn-sidebar-live-insert.test.tsx` pins that end to end
   * through `useChat` so a dropped prop fails a test rather than silently
   * disabling the cover.
   */
  placeholderTitle?: string
  /** Project scope for `queryKeys.sessionHistory`, passed through unchanged
   *  from the same `useChat({ projectId })` prop the send path's own
   *  accept-time invalidate already keys off (`sendDurableExplorerChatTurn`'s
   *  `finally` block in use-chat.ts). Only meaningful together with
   *  `queryClient`/`sessionType`. */
  projectId?: string | null
  /** Question text to pair with the turn's answer. Callers pass the durable
   *  status/current DTO's echoed `question` field (`build_details_dto` in
   *  chat_turn_service.py) — best-effort, since a process restart can drop
   *  the process-local echo registry, in which case this is `""`. Never the
   *  session's `lastQuestion`: that names the last COMPLETED turn's question,
   *  not this in-flight one. */
  question: string
  /**
   * React Query client used to deliver a durable turn's session enrichment
   * (issue #2513) to the chat list — the durable equivalent of the legacy
   * `title_state_changed` (immediate patch) + `done` (authoritative refetch)
   * WebSocket handlers in `use-websocket-message-handler.ts`. Reuses the
   * existing `queryKeys.sessionHistory` key and `chatStore.setSessions`
   * action rather than inventing a new channel.
   *
   * REQUIRED (issue #2513 finding 1): both this and `sessionType` used to be
   * optional so a caller could omit chat-list delivery — but every real
   * caller (the send path and the passive-viewer hydration path) always has
   * both available, and dropping either one silently disables the whole
   * session-enrichment feature (skips `deliverSessionEnrichment` and the
   * terminal invalidate) with the full test suite staying green, because the
   * enrichment suite only ever called this function directly with the params
   * supplied — nothing asserted the real call sites still passed them.
   * Making them required turns a dropped prop into a compile error instead
   * of a silent no-op. A caller that genuinely has neither should call a
   * variant that never touches the chat list, not pass `undefined` here.
   */
  queryClient: QueryClient
  /** Session type for `queryKeys.sessionHistory` — see `queryClient`. */
  sessionType: string
  snapshot: DurableChatTurnSnapshot
}): () => void {
  // Scoped to THIS attach's lifecycle (one per turn subscription), so a
  // terminal transition is delivered to the sidebar exactly once even though
  // `writePatch` keeps firing afterwards would it ever be called again —
  // mirrors `finishTerminalTransition`'s own one-way latch inside the
  // registry, just at this outer layer instead.
  let invalidatedSessionListOnTerminal = false
  // Non-null exactly while this attach is keeping the session list covered
  // with the in-flight chat's placeholder title (see `startSidebarCover`).
  let stopSidebarCover: (() => void) | null = null

  /**
   * Re-apply the placeholder to whatever the session-list cache holds right
   * now. `applyDurableSessionPlaceholders` is identity-preserving, so a list
   * with nothing to cover is left strictly untouched and no `setQueryData`
   * (and therefore no observer notification) happens — which is also what
   * makes calling this from a query-cache subscriber safe rather than
   * self-retriggering.
   */
  const coverSessionList = () => {
    const queries = queryClient
      .getQueryCache()
      .findAll({ queryKey: queryKeys.sessionHistory(sessionType) })
    for (const query of queries) {
      const current = query.state.data
      if (!Array.isArray(current)) continue
      const sessions = current as ChatSessionModel[]
      const covered = applyDurableSessionPlaceholders(sessions)
      if (covered === sessions) continue
      queryClient.setQueryData(query.queryKey, covered)
    }
  }

  /**
   * Keep the sidebar covered for as long as the turn runs.
   *
   * Registering the placeholder only affects list fetches that START after
   * it — but a tab that reloaded mid-turn fires its session-list request and
   * its `/turns/current` request together, so the list routinely resolves
   * through `useSessionsQuery`'s fetcher BEFORE the placeholder exists, and
   * its untitled row lands on top of any one-shot cover. With
   * `staleTime: 1000 * 30` and no further refetch until the turn ends, that
   * chat would then stay hidden for the rest of the turn. So: cover now, and
   * re-cover on every session-list success until the turn goes terminal (or
   * this attach detaches).
   */
  const startSidebarCover = () => {
    if (stopSidebarCover) return
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "success") return
      const [scope, type] = event.query.queryKey
      if (scope !== "session-history" || type !== sessionType) return
      coverSessionList()
    })
    stopSidebarCover = unsubscribe
    coverSessionList()
  }

  const finishSidebarCover = () => {
    stopSidebarCover?.()
    stopSidebarCover = null
  }

  // Activities whose out-of-transcript side effects (open-canvas refresh,
  // list invalidations) already ran for this attach. Durable snapshots carry
  // the FULL activity list on every emit, so without this each frame would
  // replay every earlier side effect (#2961).
  const appliedActivityKeys = new Set<string>()
  const applyNewActivitySideEffects = (snapshot: DurableChatTurnSnapshot) => {
    for (const activity of snapshot.activities) {
      const key = activitySideEffectKey(activity)
      if (appliedActivityKeys.has(key)) continue
      appliedActivityKeys.add(key)
      applyActivitySideEffects(activity, queryClient)
    }
  }

  /**
   * The durable transport's half of the fair-use bridge (#2625).
   *
   * Legacy routes budget data off the `done` / `error` WebSocket frames
   * (`use-websocket-message-handler.ts`); durable frames never pass through
   * that handler, so on the durable transport the composer's usage ring only
   * moved after a page refresh and a blocked user got the generic error card
   * instead of the budget gate.
   *
   * Wired HERE rather than inside `durable-chat-turn-registry.ts` on purpose:
   * the registry is a page-lifetime transport singleton with no QueryClient
   * (and no way to acquire one without a module-level global), while this
   * mirror is the durable analogue of the legacy message handler — it already
   * owns the client, and it runs on exactly the frames that carry the data
   * (`turn_event` / `turn_snapshot` / `turn_terminal` all emit through here).
   *
   * Both calls are unconditionally safe: each no-ops on a payload with no
   * fair-use data, and `routeFairUseMetadata` additionally skips the cache
   * write when the snapshot already reflects the metadata — which is the
   * common case, since a durable snapshot repeats its whole `context` on
   * every emit of the turn.
   */
  const routeDurableFairUse = (registryState: { snapshot: DurableChatTurnSnapshot }) => {
    routeFairUseMetadata(queryClient, { context: registryState.snapshot.context })
    // A refusal only ever rides a terminal error object; `routeFairUseError`
    // returns null with no side effects for every other (or absent) error.
    routeFairUseError(queryClient, registryState.snapshot.error)
  }

  const deliverSessionEnrichment = (snapshotWithSession: DurableChatTurnSnapshot) => {
    const enrichedSession = snapshotWithSession.session
    if (!enrichedSession) return

    // Immediate patch — mirrors `title_state_changed`'s
    // `store.setSessions(store.sessions.map(...))` +
    // `queryClient.setQueriesData(...)` pair exactly, just fed from the
    // durable snapshot's `session` field instead of a WS frame. Best-effort
    // and idempotent: re-applying the same (or a still-partial) enrichment on
    // a later emit never fights itself — see
    // `applyDurableSessionEnrichmentToSessions`'s never-blank-title guard.
    const store = chatStore.getState()
    store.setSessions(applyDurableSessionEnrichmentToSessions(store.sessions, enrichedSession))
    queryClient.setQueriesData<ChatSessionModel[]>(
      { queryKey: queryKeys.sessionHistory(sessionType) },
      (old) => (old ? applyDurableSessionEnrichmentToSessions(old, enrichedSession) : old)
    )
  }

  const writePatch = (
    registryState: Parameters<typeof mapDurableChatTurnStateToSessionPatch>[0]["registryState"]
  ) => {
    const sessionState = chatStore.getState().sessionStates[identity.sessionId]
    const currentMessages = sessionState?.messages ?? []
    // Never overwrite an already-rendered question with an empty one (issue
    // #2513 finding 3). `question` is best-effort per-attach: a re-attach
    // (session-id churn, a route remount, switching chats and back mid-turn)
    // can legitimately echo `""` even though an earlier attach (or the
    // original send) already rendered the real question for this SAME turn.
    // Only reused when it actually belongs to this turn — a stale answer
    // from a different/prior turn must never leak in as a question.
    const existingAnswer = sessionState?.streamingAnswer
    const existingQuestion =
      existingAnswer && existingAnswer[1].turn_id === identity.turnId ? existingAnswer[0] : ""
    const effectiveQuestion = question || existingQuestion
    const patch = mapDurableChatTurnStateToSessionPatch({
      registryState,
      question: effectiveQuestion,
      currentMessages,
      currentSteerNotes: sessionState?.steerNotes,
      currentQueuedMessages: sessionState?.queuedMessages,
      isMessageQueueEnabled,
      sessionType,
    })
    chatStore.getState().updateSession(identity.sessionId, {
      ...patch,
      durableTurn: registryState.terminal ? null : identity,
      durableTurnReconnecting: !registryState.terminal && registryState.reconnecting,
    })

    routeDurableFairUse(registryState)
    deliverSessionEnrichment(registryState.snapshot)
    // Same side effects the legacy websocket handler runs per activity frame
    // — the durable path only ever copied `snapshot.activities` into state,
    // so a tool that rewrote the open canvas document never refreshed it.
    applyNewActivitySideEffects(registryState.snapshot)

    // Sidebar cover for the in-flight turn. A durable session has no
    // server-side title until completion, so ANY session-list fetch that
    // lands mid-turn (window focus, a 30s-stale remount, another module's
    // invalidate, or the very first list load after a mid-turn reload) hands
    // the sidebar an untitled row that `sessions.filter((s) => s?.title)`
    // drops. Covering from here — rather than only from the send path —
    // means a reloaded tab and a second viewer get the same cover the sender
    // does. Idempotent: re-registering the same title on every emit is a
    // no-op — it neither notifies nor broadcasts (issue #3323 item C2).
    //
    // Released on the FIRST terminal emit, unconditionally (not behind
    // `invalidatedSessionListOnTerminal`, which only debounces the refetch):
    // by then enrichment has named the chat, so the authoritative list
    // decides its name from here on, and a chat that genuinely ended up with
    // no title is filtered out exactly like legacy's. `delete` on an
    // unregistered id is a no-op, so a turn that never registered — or one
    // already released by another attach for the same session — costs
    // nothing.
    if (registryState.terminal) {
      releaseDurableSessionPlaceholder(identity.sessionId, identity.turnId)
      finishSidebarCover()
    } else if (placeholderTitle) {
      registerDurableSessionPlaceholder(identity.sessionId, identity.turnId, placeholderTitle)
      startSidebarCover()
    }

    // Authoritative refresh — mirrors the legacy `done` handler's
    // `queryClient.invalidateQueries(queryKeys.sessionHistory(...))`: a
    // completed/failed/cancelled/interrupted turn's `message_count` (and
    // anything enrichment didn't manage to publish live, e.g. a lease lost
    // right before the append) is only guaranteed correct once refetched from
    // the authoritative session read. Fires once per attach regardless of
    // whether THIS emit carried a `session` field, matching `done`'s
    // unconditional invalidate.
    if (registryState.terminal && !invalidatedSessionListOnTerminal) {
      invalidatedSessionListOnTerminal = true
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(sessionType, projectId),
      })
      // Same event, other windows (issue #2548). Guarded so a listener that
      // throws can never break the attach's own terminal bookkeeping.
      try {
        onSessionListSettled?.()
      } catch (error) {
        logger.warn("Cross-window session hint failed on durable turn settle", error as Error)
      }
    }
  }

  const initialState = durableChatTurnRegistry.register({
    ...identity,
    getToken,
    snapshot,
    authoritative,
  })
  writePatch(initialState)

  // Issue #2513 finding 17: a listener that outlives the turn keeps this
  // registry entry ineligible for pruning forever — `terminalEntriesToDispose`
  // only disposes an entry once its `listeners` set is EMPTY, regardless of
  // `maxRetainedTerminalTurns` — and the send path
  // (`sendDurableExplorerChatTurn` in use-chat.ts) intentionally discards
  // this function's own returned disposer: that attach's registry mirror is
  // meant to outlive the request that created it, not be tied to its
  // lifecycle, so there is no natural point in THAT caller to invoke it.
  // Self-clean instead of relying on every caller to manage a disposer
  // correctly: never subscribe at all when the turn is ALREADY terminal by
  // the time this attach runs (`writePatch` above already applied the one
  // and only patch a terminal turn will ever produce — there is nothing left
  // to listen for), and unsubscribe the instant a live subscription's own
  // emit goes terminal. Calling the returned disposer again later (a caller
  // that DOES track it, e.g. `useDurableChatTurnHydration`'s `detachRef`, on
  // unmount) stays safe either way — `Set.delete` on an already-removed
  // listener is a no-op.
  let unsubscribeFromRegistry: () => void = () => {}
  if (!initialState.terminal) {
    unsubscribeFromRegistry = durableChatTurnRegistry.subscribe(identity, (registryState) => {
      writePatch(registryState)
      if (registryState.terminal) unsubscribeFromRegistry()
    })
  }

  // A SECOND, independent subscription dedicated only to releasing the
  // placeholder — deliberately outliving `unsubscribeFromRegistry` above.
  // That one is the detachable UI mirror: the return value below tears it
  // down on session-switch/unmount by design, since only the UI needs to
  // stop. But `writePatch` — the only thing that calls
  // `releaseDurableSessionPlaceholder` — is reachable exclusively through
  // that same listener, so a passive/reloaded tab that switches sessions
  // before the turn settles unsubscribed the one path that would ever
  // release it: the registry reaches terminal with no listener left, and
  // the module-level placeholder (registered above, and deliberately NOT
  // torn down by this function's own returned disposer — see its comment)
  // survives for the page's lifetime, continuing to cover the sidebar with
  // "New chat" long after the turn actually finished. `clearStaleDurableIdentity`
  // does not release it either — it only clears `durableTurn`, never the
  // placeholder map. Self-cleans exactly like the UI mirror's own listener
  // (same "never subscribe if already terminal, unsubscribe on first
  // terminal emit" contract) — it just isn't reachable from `return () =>`.
  if (placeholderTitle && !initialState.terminal) {
    let unsubscribePlaceholderGuard: () => void = () => {}
    unsubscribePlaceholderGuard = durableChatTurnRegistry.subscribe(identity, (registryState) => {
      if (!registryState.terminal) return
      releaseDurableSessionPlaceholder(identity.sessionId, identity.turnId)
      unsubscribePlaceholderGuard()
    })
  }

  return () => {
    // Detaching the UI mirror also drops the query-cache subscriber — leaving
    // it behind would keep this attach alive for the page's lifetime. The
    // placeholder itself deliberately survives detach (it is released only on
    // the turn's terminal emit, by the long-lived guard above): a user who
    // switches chats mid-turn must still find the running chat in the
    // sidebar when a refetch lands, and `useSessionsQuery`'s fetcher covers
    // that on its own.
    finishSidebarCover()
    unsubscribeFromRegistry()
  }
}

export type UseDurableChatTurnHydrationOptions = {
  chatStore: ChatStore
  /** Fail-closed: hydration only ever reads/registers when explicitly
   *  enabled (the durable gate for this mode). Off by default everywhere
   *  this hook is mounted without the gate, so it is a strict no-op. */
  enabled: boolean
  getToken: () => string | undefined
  /** Threaded through to `attachDurableChatTurnToSession` — see its own doc
   *  comment. A passive viewer settles steer chips at turn end exactly as the
   *  sending tab does, so both must read the same flag. */
  isMessageQueueEnabled?: boolean
  /** Threaded straight through to `attachDurableChatTurnToSession` — see its
   *  own `onSessionListSettled` doc comment. A passive viewer that followed
   *  a turn it did not send is just as entitled to hint the browser's other
   *  windows as the sender is. */
  onSessionListSettled?: () => void
  /** Sidebar placeholder title for an in-flight turn discovered on hydration
   *  — see `attachDurableChatTurnToSession`'s `placeholderTitle`. This is the
   *  branch a user who reloads mid-turn actually hits, so it is the one that
   *  most needs the cover. */
  placeholderTitle?: string
  /** Project scope for `queryKeys.sessionHistory` — see
   *  `attachDurableChatTurnToSession`'s `projectId`. */
  projectId?: string | null
  /** Threaded straight through to `attachDurableChatTurnToSession` so a
   *  passive viewer (a second tab/device hydrating an in-flight turn it
   *  didn't send) delivers the SAME session-enrichment mirror the sender
   *  gets — issue #2513's "a viewer that is not the sender ... must also get
   *  the update" requirement. REQUIRED for the same reason as there
   *  (issue #2513 finding 1) — this hook's one production caller
   *  (`use-chat.ts`) always has a concrete `QueryClient` on hand. */
  queryClient: QueryClient
  sessionId: string | null
  /** Session type for `queryKeys.sessionHistory` — see
   *  `attachDurableChatTurnToSession`'s `sessionType`. */
  sessionType: string
}

/**
 * Bounded poll interval for the "follow this chat" loop below: while a
 * session is open, the durable gate is on, and no durable turn is currently
 * attached to it, keep asking `GET /turns/current` on this cadence until one
 * appears (a real user report: two windows open on the same chat side by
 * side, a turn started in one, the other — the "passive" viewer — never
 * discovered it without a manual refresh, because the one-shot hydration
 * effect below asks exactly once, at session-open, and permanently latches
 * out afterward).
 *
 * 5s bounds discovery to "a few seconds" (the reported bug's own bar) while
 * keeping the steady-state cost of an open, turn-less chat at 12 requests/
 * minute per open tab — small for a per-session, per-user status read, and
 * `focusManager`-gated to genuinely zero while the tab is hidden/
 * backgrounded (see the poll effect's own comment). Matches the existing
 * "poll for a state change" convention elsewhere in this codebase (e.g.
 * `use-projects.ts`'s `POLLING_INTERVAL_MS`, `video-insight-page.tsx`'s
 * `refetchInterval: 5000`) rather than inventing a different cadence.
 *
 * Deliberately unbounded in wall-clock duration (no "give up after N
 * minutes" cap, unlike e.g. `use-projects.ts`'s `MAX_POLLING_DURATION_MS`):
 * a chat window staying open indefinitely with nothing happening yet is
 * exactly the reported scenario (a passive tab left open side by side), not
 * a one-off job that either resolves soon or was abandoned. A cap here would
 * silently reintroduce the bug for any viewer who leaves the passive tab
 * open longer than the cap.
 */
export const DURABLE_TURN_POLL_INTERVAL_MS = 5000

/**
 * Backoff cap for the delay between poll retries while consecutive failures
 * accumulate — doubles per failure so a flaky-but-recovering backend is
 * retried with increasing patience instead of hammering it every
 * `DURABLE_TURN_POLL_INTERVAL_MS`, capped here so it never drifts
 * unreasonably far past the normal cadence.
 *
 * Issue #2513 finding 18: this used to be paired with a "give up after N
 * consecutive failures" cutoff applied to every failure alike — including an
 * ordinary transient one (a network blip, a brief backend deploy, a Cosmos
 * hiccup). Three consecutive failures at this backoff is only ~35 seconds,
 * after which the poll permanently died for the rest of the mount: no
 * further requests, no further logs, until the user changed session or
 * reloaded — silently reintroducing the exact passive-viewer bug this poll
 * exists to fix, for any tab that happened to be open across a routine
 * deploy. Give-up now keys on the failure's own classification (see
 * `isPermanentPollFailure`), not a raw streak count: a transient failure
 * backs off up to this cap and keeps retrying indefinitely, matching the
 * "unbounded in wall-clock duration" property `DURABLE_TURN_POLL_INTERVAL_MS`'s
 * own doc comment already promises for the idle-but-reachable case.
 */
export const DURABLE_TURN_POLL_MAX_BACKOFF_MS = DURABLE_TURN_POLL_INTERVAL_MS * 4

/**
 * Historical note (issue #2513 finding 12/18-D): the poll's own
 * `invalidateSessionMessages` nudge (see that function's doc comment for the
 * "passive window can miss a completed turn" scenario it exists to fix) used
 * to fire unconditionally on every 204/already-terminal response, then —
 * after a first fix — on a 30-second throttle. Both still refetched the full
 * message history on a fixed cadence for as long as a durable-enabled
 * session stayed open with no active turn, whether or not anything had
 * changed since the last nudge; throttling only slowed the waste, it didn't
 * bound it. The poll now compares the `X-Chat-Turn-Revision` header
 * `getCurrentDurableChatTurnApi` exposes tick over tick (see
 * `lastSeenRevision`/`revisionGatedInvalidateSessionMessages` inside the poll
 * effect below) and nudges only when that value actually changes — an idle
 * session nudges once, then never again until a turn genuinely starts,
 * finishes, or is cancelled.
 */

/**
 * True for a poll failure that retrying can never resolve: the backend has
 * definitively said this caller may not have durable turns on this session
 * (403 `code: "durable_turns_disabled"` — the only 403 `GET .../turns/current`
 * itself can produce, see `get_current_turn` in
 * app/backend/routes/chat_turns.py) or that the session genuinely doesn't
 * exist for this caller (404 `{"error": "Session not found"}` from that same
 * route). Anything else — no `ApiError` at all (a raw network failure never
 * reaches that far, see `classifyFetchFailure` in api/client/_shared.ts), a
 * 5xx, a timeout — is presumed transient: the poll must keep trying, backed
 * off, rather than mistake "the backend blipped for a bit" for "this session
 * will never have durable turns."
 *
 * Deliberately narrower than a bare `status === 404` (a prior version of
 * this fix used exactly that, incorrectly): a 404 can ALSO be the SPA
 * catch-all's unknown-route response, reachable during a routine rolling
 * deploy while the old build's JS is still calling a route the new build
 * momentarily doesn't serve (or vice versa) — a purely transient condition
 * that clears itself once the deploy finishes. Treating that as permanent
 * would silently reintroduce the exact bug issue #2513 finding 18 fixed, for
 * any tab left open across a deploy. `isUnknownApiRouteError` (shared with
 * `shouldFallbackToLegacyTransport`, which distinguishes the identical two
 * shapes for a different call) is the one place that distinction lives.
 */
function isPermanentPollFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.status === 403) return error.code === "durable_turns_disabled"
  if (error.status === 404) return !isUnknownApiRouteError(error)
  return false
}

/**
 * Returning to a chat (mount, session switch, reload) must hydrate a durable
 * turn's canonical status from the durable status/current reads — never by
 * restarting execution, and never by duplicating the final message. This
 * hook does two things: on each new `sessionId` (while `enabled`), read
 * `GET .../turns/current` once immediately, and if a turn is currently
 * active for the session, attach it via `attachDurableChatTurnToSession`
 * (below). If none is active (204), that one-shot ask is a no-op — the
 * session's `messages` history (loaded separately by `use-chat-history.ts`)
 * already covers every completed turn — but the hook then keeps asking on
 * `DURABLE_TURN_POLL_INTERVAL_MS` for as long as the session stays open and
 * unattached, so a turn that starts LATER (in this tab or another) is still
 * discovered without a reload. See `DURABLE_TURN_POLL_INTERVAL_MS`'s own
 * comment for the interval/cost tradeoff.
 */
export function useDurableChatTurnHydration({
  chatStore,
  enabled,
  getToken,
  isMessageQueueEnabled = false,
  onSessionListSettled,
  placeholderTitle,
  projectId,
  queryClient,
  sessionId,
  sessionType,
}: UseDurableChatTurnHydrationOptions): void {
  const hydratedSessionRef = useRef<string | null>(null)
  const detachRef = useRef<(() => void) | null>(null)

  // Always-current refs for `getToken`/`chatStore` (issue #2513 finding 1).
  // The effect below must NOT depend on these by identity: a caller that
  // passes a fresh arrow (e.g. `() => settingsService.token`) on every render
  // would otherwise re-run the effect on every unrelated re-render of the
  // host component. Each re-run fires the PREVIOUS run's cleanup, which sets
  // `cancelled = true` for that run's in-flight `/turns/current` promise —
  // and because `hydratedSessionRef.current` is already set to this
  // sessionId, the re-run's guard returns early instead of re-issuing the
  // read. A single unrelated re-render while the read is in flight would
  // permanently lose the attach. Reading through refs keeps the effect's own
  // deps stable across such churn.
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const chatStoreRef = useRef(chatStore)
  chatStoreRef.current = chatStore
  // Same churn-tolerance as `getTokenRef`/`chatStoreRef` above — these are
  // read-only passthroughs to `attachDurableChatTurnToSession`, not part of
  // what decides whether/when to (re-)hydrate.
  const queryClientRef = useRef(queryClient)
  queryClientRef.current = queryClient
  const sessionTypeRef = useRef(sessionType)
  sessionTypeRef.current = sessionType
  const projectIdRef = useRef(projectId)
  projectIdRef.current = projectId
  const placeholderTitleRef = useRef(placeholderTitle)
  placeholderTitleRef.current = placeholderTitle
  const onSessionListSettledRef = useRef(onSessionListSettled)
  onSessionListSettledRef.current = onSessionListSettled

  // Reactive "is a durable turn currently attached to THIS session" signal —
  // the same `durableTurn` session-state field the send path
  // (`sendDurableExplorerChatTurn`'s own `existingDurableTurn` guard in
  // use-chat.ts) and the save-guard watchdog already treat as the canonical
  // answer to that question, rather than a hook-local latch. Read via the
  // store HOOK (not `.current.getState()`) so it participates in React's
  // render cycle: the poll effect below is keyed on it, so attaching (from
  // ANY path — this poll, the one-shot ask above, or a fresh send in this
  // very tab) reactively tears the poll down, and a later terminal
  // transition clearing it back to `null` reactively starts the poll back
  // up. That is the entire "stop while attached / resume once it settles"
  // contract — no bespoke bookkeeping needed for it beyond this one read.
  const isAttached = chatStore((state) =>
    sessionId !== null ? Boolean(state.sessionStates[sessionId]?.durableTurn) : false
  )

  // Shared by both the one-shot hydration effect and the poll effect below:
  // given a `/turns/current` response that names an active turn, replace
  // whatever this hook was previously mirroring and attach the new one.
  // Reads every dependency through the refs above, so it is safe to close
  // over from an effect that doesn't itself re-run on every render — it
  // always sees the latest `chatStore`/`getToken`/etc. at call time.
  const attachFromCurrent = (current: DurableChatTurnCurrentResponse): void => {
    const identity: DurableChatTurnIdentity = {
      sessionId: current.sessionId,
      turnId: current.turnId,
      epoch: current.epoch,
    }
    // The backend explicitly echoes the originating question back
    // (`build_details_dto` — "the frontend hydration depends on both").
    // Never fall back to the session's `lastQuestion`: that reflects the
    // last COMPLETED turn's question, not this in-flight turn's — using it
    // would mislabel the answer with the wrong (previous) question.
    detachRef.current?.()
    detachRef.current = attachDurableChatTurnToSession({
      isMessageQueueEnabled,
      chatStore: chatStoreRef.current,
      getToken: getTokenRef.current,
      identity,
      // Read through a ref, not captured: this is a `useCallback` value that
      // changes identity whenever the publisher's scope does, and the attach
      // must always call the CURRENT one.
      onSessionListSettled: () => onSessionListSettledRef.current?.(),
      placeholderTitle: placeholderTitleRef.current,
      projectId: projectIdRef.current,
      question: current.question ?? "",
      queryClient: queryClientRef.current,
      sessionType: sessionTypeRef.current,
      snapshot: current.snapshot,
      // Honest signal, not a guess (issue #2513 finding 2) — currently
      // always `false` in practice since neither `/turns/current` nor this
      // poll's own re-read of it ever attaches a covering snapshot
      // document, but wired to the real field rather than hardcoded so a
      // future backend change flows through.
      authoritative: Boolean(current.replayGap),
    })
  }

  /**
   * Shared by both `/turns/current` call sites below (the one-shot
   * hydration ask and the poll): a 204 (no active turn) can still leave
   * THIS session's `sessionStates[sid].durableTurn` field pointing at a
   * STALE identity. Nothing else clears it — the detach effect at the
   * bottom of this hook only drops the registry listener on unmount/
   * session-switch (per the registry's own "detaching the last listener
   * must never stop the turn subscription" contract), and a turn that goes
   * terminal with nobody listening never gets the chance to write
   * `durableTurn: null` via `writePatch` either (that write only happens
   * from an active registry subscription callback). Concretely: attach a
   * turn, unmount/switch away (detach only drops the listener), let the
   * turn go terminal with no listener attached, then remount on the same
   * session — `durableTurn` is still the old identity. Left uncleared,
   * `isAttached` below (derived from this exact field) stays permanently
   * true and the poll effect never installs again — the original
   * passive-viewer bug, restored.
   *
   * Only clears when the registry itself confirms the stored identity is
   * actually gone (pruned) or has already settled into terminal — never on
   * a bare "no response yet" basis, so an identity the registry still
   * considers live is never torn down here. Re-reads the field immediately
   * before writing so a fresh same-tab send (or another attach) that lands
   * between the registry check and this write is never clobbered.
   *
   * R1 (durable chat turns risk review): also settles `isLoading`,
   * `isStreaming`, and `isAwaitingSave` — never just `durableTurn`/
   * `durableTurnReconnecting`. Detaching mid-turn (switching chats,
   * unmounting) only ever drops the registry LISTENER — per
   * `attachDurableChatTurnToSession`'s own "detaching...must never stop the
   * turn subscription" contract — never the loading/streaming/save-guard
   * flags a live subscription's `writePatch` would have settled on the
   * turn's terminal transition. If nobody was listening when the turn
   * actually finished, this 204 read (an identity this session still has
   * recorded as live, but the backend now reports no active turn) is the
   * ONLY place left that can release them: both `useSaveGuardWatchdog`
   * backstops are structurally unable to — arm 1 bails while `isStreaming ||
   * isLoading || hasStreamingAnswer`, and arm 2 needs `durableTurn`, which
   * this same write nulls out — so leaving them out here would wedge the
   * composer for the rest of the page's life (only a full reload, which
   * re-initializes session state from scratch, would clear it).
   *
   * `streamingAnswer` only clears once its content is confirmed covered by
   * `messages` — matched by this exact turn's id, the same key
   * `mergeDurableChatTurnFinalMessage` merges on — never on the bare
   * "no active turn" signal alone. A `failed` durable turn is never
   * persisted (`_PARTIAL_ANSWER_STATUSES` excludes it in
   * chat_turn_runner.py), and even a persisted cancelled/interrupted/
   * completed answer can still be waiting on `useSessionMessages`'s own
   * cache to refresh — so an unmatched turn keeps its last-known content,
   * marked complete like a locally-stopped legacy turn (mirrors
   * `use-chat.ts`'s `getLocallyStoppedState` convention), rather than
   * silently vanishing.
   *
   * Adversarial-review follow-up: when `registryState` is DEFINED and
   * terminal, the registry entry is still holding the REAL final state —
   * `writePatch` only stops running while a listener is subscribed (per
   * `attachDurableChatTurnToSession`'s own "detaching...must never stop the
   * turn subscription" contract), so content produced between this attach's
   * detach and the turn's actual terminal transition landed in the
   * registry, never in this store's `streamingAnswer` copy (that field is
   * frozen at whatever the last live `writePatch` wrote). Reusing the exact
   * `mapDurableChatTurnStateToSessionPatch` mapping `writePatch` itself uses
   * — rather than reconstructing a narrower patch from the stale
   * `streamingAnswer` alone — means a listener-less settle merges the SAME
   * final answer into `messages` (turn_id-deduped) that a live tab would
   * have shown, instead of stamping a truncated partial `completed: true`
   * and leaving it to duplicate once `messages` eventually refetches the
   * real row. Only falls back to the narrower, stale-`streamingAnswer`-based
   * reconstruction below when `registryState` is itself `undefined` — the
   * entry has been pruned and there is genuinely nothing more authoritative
   * left to read.
   */
  const clearStaleDurableIdentity = (sid: string): void => {
    const store = chatStoreRef.current
    const stored = store.getState().sessionStates[sid]?.durableTurn
    if (!stored) return
    const registryState = durableChatTurnRegistry.get(stored)
    if (registryState !== undefined && !registryState.terminal) return
    const latestState = store.getState().sessionStates[sid]
    if (latestState?.durableTurn !== stored) return

    const currentMessages = latestState.messages ?? []
    const staleStreamingAnswer = latestState.streamingAnswer
    // Same best-effort question recovery `writePatch` itself does when its
    // own per-attach `question` echo is empty: only reused when it actually
    // belongs to THIS turn, never leaked in from a stale/different one.
    const recoveredQuestion =
      staleStreamingAnswer && staleStreamingAnswer[1].turn_id === stored.turnId
        ? staleStreamingAnswer[0]
        : ""

    if (registryState !== undefined) {
      const patch = mapDurableChatTurnStateToSessionPatch({
        registryState,
        question: recoveredQuestion,
        currentMessages,
        // Same #2469 turn-end chip settlement `writePatch` runs while a
        // listener is attached — without these, `settleSteerNotesForPatch`
        // no-ops (`!notes?.length`) and the next send wipes the missed note.
        currentSteerNotes: latestState.steerNotes,
        currentQueuedMessages: latestState.queuedMessages,
        isMessageQueueEnabled,
        sessionType: sessionTypeRef.current,
      })
      store.getState().updateSession(sid, {
        ...patch,
        durableTurn: null,
        durableTurnReconnecting: false,
      })
      return
    }

    const answerAlreadyInMessages = currentMessages.some(
      ([, response]) => response.turn_id === stored.turnId
    )
    // Resolved ahead of the write (rather than a nested ternary inline)
    // purely to keep each branch's intent legible: `null` once the answer is
    // confirmed covered by `messages`; the stale partial marked complete
    // when there is one to preserve; `undefined` (omit the key, leaving
    // whatever is already there — there is nothing to settle) only when
    // neither applies.
    let nextStreamingAnswer: SessionState["streamingAnswer"] | undefined
    let preservedPartial = false
    if (answerAlreadyInMessages) {
      nextStreamingAnswer = null
    } else if (staleStreamingAnswer) {
      nextStreamingAnswer = [
        staleStreamingAnswer[0],
        { ...staleStreamingAnswer[1], completed: true },
      ]
      preservedPartial = true
    }

    // Registry entry is gone, so the mapper above cannot run — still settle
    // chips with the same #2469 rules a live terminal emit uses.
    const notes = latestState.steerNotes ?? []
    const settlement =
      notes.length > 0 ? settleSteerNotesAtTurnEnd(notes, isMessageQueueEnabled) : undefined

    store.getState().updateSession(sid, {
      durableTurn: null,
      durableTurnReconnecting: false,
      isLoading: false,
      isStreaming: false,
      isAwaitingSave: false,
      // Same reasoning as the terminal patch in `durable-chat-turn-state.ts`
      // (#2813): the turn is over, so a requested cancel is no longer pending.
      // A stop-and-redirect waits on this before auto-sending.
      cancelPending: false,
      ...(nextStreamingAnswer !== undefined ? { streamingAnswer: nextStreamingAnswer } : {}),
      // Mirrors `getLocallyStoppedState`'s convention exactly (use-chat.ts):
      // whenever a partial answer is preserved rather than dropped, Retry/
      // Continue must stay available for it.
      ...(preservedPartial ? { canContinue: true } : {}),
      ...(settlement
        ? {
            steerNotes: settlement.steerNotes,
            ...(settlement.queuedMessagesToAppend.length > 0
              ? {
                  queuedMessages: [
                    ...(latestState.queuedMessages ?? []),
                    ...settlement.queuedMessagesToAppend,
                  ],
                }
              : {}),
          }
        : {}),
    })
  }

  /**
   * Best-effort nudge for a session's message HISTORY (issue #2513 finding
   * 12) — distinct from `clearStaleDurableIdentity`'s job, which only tidies
   * up state THIS window already knew about. A passive window can miss a
   * turn ENTIRELY: it starts and settles between two poll ticks (or between
   * mount and the one-shot hydration read resolving), so `/turns/current`
   * only ever reports the terminal turn as a bare 204 (or a 200 this poll
   * deliberately declines to attach to, just below) — never as a value this
   * window's own `durableTurn` field was ever set to. `clearStaleDurableIdentity`
   * is a correct no-op for exactly that case (there is nothing locally
   * tracked to clear), but a no-op there was ALSO the reason nothing else
   * ever told `useSessionMessages` (`use-chat-history.ts`) new content might
   * exist — that query has `refetchOnWindowFocus: false`, so without an
   * explicit nudge like this one, the conversation could stay stale in this
   * window indefinitely.
   *
   * Deliberately `invalidateQueries`, not a forced `refetchQueries`: cheap
   * when the query has no active observer (this session isn't even the one
   * currently rendered in this window — the common case for a background
   * poll tick), and reactively refetches when it does. Matches the base key
   * only (`queryKeys.sessionMessages(sid)`, no cache-buster suffix), which
   * — react-query's default prefix matching — also invalidates any
   * cache-busted variant `useSessionMessages` may have queried under.
   */
  const invalidateSessionMessages = (sid: string): void => {
    void queryClientRef.current.invalidateQueries({ queryKey: queryKeys.sessionMessages(sid) })
  }

  useEffect(() => {
    if (!enabled || !sessionId) return undefined
    if (hydratedSessionRef.current === sessionId) return undefined
    hydratedSessionRef.current = sessionId

    let cancelled = false

    void getCurrentDurableChatTurnApi(sessionId, getTokenRef.current())
      .then((current) => {
        if (cancelled) {
          // This run was superseded (unmount or a genuine sessionId/enabled
          // change) before the read resolved, and never attached. Release
          // the guard — but only if it still names THIS run's sessionId, so
          // a newer run that has already claimed it is never clobbered —
          // so a later mount/session-switch back to this same id can retry
          // instead of being permanently latched out.
          if (hydratedSessionRef.current === sessionId) hydratedSessionRef.current = null
          return
        }
        if (!current) {
          clearStaleDurableIdentity(sessionId)
          // See `invalidateSessionMessages`'s own doc comment: a turn that
          // started and settled before this very first read could resolve
          // here is the narrowest instance of issue #2513 finding 12.
          invalidateSessionMessages(sessionId)
          return
        }
        // A session switch mid-flight may have already hydrated (or started
        // hydrating) a newer session by the time this resolves; only attach
        // if we're still the hydration owner for this session id.
        if (hydratedSessionRef.current !== sessionId) return
        attachFromCurrent(current)
      })
      .catch((error: unknown) => {
        logger.warn("Durable chat turn hydration failed", error as Error)
        if (hydratedSessionRef.current === sessionId) hydratedSessionRef.current = null
        // R8 (durable chat turns risk review): a rejected read must not
        // leave a stale `durableTurn` behind any more than a successful 204
        // does (the `!current` branch above) — an identity this call can
        // neither confirm nor refute is exactly the kind `clearStaleDurableIdentity`
        // is built to safely no-op on (it only ever clears when the registry
        // itself confirms the stored identity is gone or terminal). Without
        // this, a single flaky read latches `durableTurn` stale for the rest
        // of the page's life: `isAttached` (derived from this exact field)
        // stays permanently true, which both disables the "follow this chat"
        // discovery poll below for a turn that starts later, and would route
        // every subsequent Stop in this chat through the durable branch —
        // including for an unrelated legacy turn — instead of a clean no-op.
        clearStaleDurableIdentity(sessionId)
      })

    return () => {
      cancelled = true
    }
  }, [enabled, sessionId])

  // "Follow this chat" (the passive-viewer fix): while the durable gate is
  // on, a session is open, and no durable turn is attached to it, keep
  // asking `GET /turns/current` on `DURABLE_TURN_POLL_INTERVAL_MS` until one
  // appears. `isAttached` flipping true — no matter which of the three
  // attach paths caused it — tears this effect down before its very next
  // tick; `isAttached` flipping back to `false` on a later terminal
  // transition starts it again. Deliberately does NOT fire an immediate
  // first tick on mount: the one-shot effect above already owns that ask,
  // so an immediate tick here would double the request on every fresh
  // session-open. The one case this costs is resuming right after a turn
  // settles, where the very next ask waits up to one full interval instead
  // of firing instantly — an acceptable trade for never duplicating the
  // open-time request, and still within the "a few seconds" bar.
  useEffect(() => {
    if (!enabled || !sessionId || isAttached) return undefined

    const pollSessionId = sessionId
    let cancelled = false
    let requestInFlight = false
    // Consecutive-failure counter driving the backoff delay below (issue:
    // durable activity-latency review, finding 4). Reset to 0 on every
    // response that actually completes — success OR a definitive 204 — so a
    // single blip never leaves the next tick waiting longer than the normal
    // cadence once the backend is reachable again. No longer drives a
    // give-up cutoff of its own (issue #2513 finding 18) — see
    // `permanentGiveUp` below for why the poll stops.
    let consecutiveFailures = 0
    // Set only when a tick's failure is classified permanent (see
    // `isPermanentPollFailure`) — an ordinary transient failure, no matter
    // how long the streak runs, never sets this and the poll keeps retrying
    // indefinitely at the capped backoff.
    let permanentGiveUp = false
    // Set when a tick was skipped because the tab was hidden. Regaining focus
    // then asks at once instead of waiting out a timer the hidden tab may
    // have throttled for minutes (issue #3354).
    let skippedWhileHidden = false
    let timeoutId: number | undefined
    // `undefined` (never observed a revision yet in this effect instance) so
    // the very first 204/already-terminal tick after this poll (re)starts —
    // most commonly right after a turn this window WAS tracking just went
    // terminal — nudges immediately: any real revision value (including
    // `null`, for a session that has never had a durable turn) differs from
    // `undefined`. Every tick after that only nudges when the revision
    // itself changes, not on a fixed cadence (issue #2513, finding 18/D).
    let lastSeenRevision: string | null | undefined

    const revisionGatedInvalidateSessionMessages = (revision: string | null): void => {
      if (revision === lastSeenRevision) return
      lastSeenRevision = revision
      invalidateSessionMessages(pollSessionId)
    }

    // Self-rescheduling `setTimeout` (replacing the previous fixed
    // `setInterval`) so the delay before the NEXT tick can vary: normal
    // cadence on success, back off on failure, and stop rescheduling
    // entirely once give-up is reached. `tick` referencing itself here is
    // safe — by the time either callback actually runs, this `const` is
    // long since assigned.
    const tick = (): void => {
      // Overlap guard: scheduling only ever happens from this tick's own
      // `finally` below (or the mount-time kick-off after it), so this
      // should never actually be reachable — kept as a cheap defensive
      // check rather than a load-bearing one.
      if (cancelled || requestInFlight) return
      // Same signal @tanstack/react-query's own `refetchInterval` gates on
      // internally (`refetchIntervalInBackground` defaults to false, checked
      // against exactly this) — reused directly rather than adding a second
      // visibility mechanism. Skips the network call entirely while the tab
      // is hidden/backgrounded, and reschedules at the normal cadence (not a
      // failure, so no backoff) so polling resumes on its own the moment the
      // tab regains visibility — no separate "catch up" path needed.
      if (!focusManager.isFocused()) {
        skippedWhileHidden = true
        timeoutId = window.setTimeout(tick, DURABLE_TURN_POLL_INTERVAL_MS)
        return
      }
      skippedWhileHidden = false
      requestInFlight = true
      let tickRevision: string | null = null
      void getCurrentDurableChatTurnApi(pollSessionId, getTokenRef.current(), (revision) => {
        tickRevision = revision
      })
        .then((current) => {
          if (cancelled) return
          consecutiveFailures = 0
          if (!current) {
            clearStaleDurableIdentity(pollSessionId)
            // See `invalidateSessionMessages`'s own doc comment: this is the
            // main instance of issue #2513 finding 12 — a turn that started
            // and fully settled between two poll ticks is invisible to
            // `clearStaleDurableIdentity` (nothing was ever tracked locally
            // to clear) just as much as it is to a live subscription.
            // Revision-gated (issue #2513 finding 18/D) rather than firing on
            // literally every tick — an idle session with no active turn
            // hits this branch every single poll, forever.
            revisionGatedInvalidateSessionMessages(tickRevision)
            return
          }
          // A `/turns/current` 200 can still describe an ALREADY-TERMINAL
          // turn: the backend only 204s on the NEXT read after finalize
          // clears the session's active-turn pointer, so a poll tick that
          // lands in that narrow window sees the just-finished turn one
          // more time (issue: durable activity-latency review, finding 3).
          // Attaching it here would immediately detach again (the registry
          // entry is already terminal) while still paying for a fresh
          // registry listener/subscriber and a session-history invalidate —
          // and since this effect only runs while UNATTACHED, every
          // subsequent tick would repeat that same pointless cycle for as
          // long as the session stays open. NOT attaching means the
          // snapshot's content never reaches this window's `messages`
          // through `attachFromCurrent`'s own merge either, though — so the
          // same nudge as the 204 branch above still applies here (issue
          // #2513 finding 12): cheap, and correct, either way.
          if (isTerminalStatus(current.status)) {
            revisionGatedInvalidateSessionMessages(tickRevision)
            return
          }
          // Re-check the LIVE store, not a closed-over flag: a fresh send in
          // this same tab, or an earlier tick's own attach, can land between
          // this request going out and its response coming back. The
          // registry's own `register()`/`subscribe()` (in
          // durable-chat-turn-registry.ts) ARE idempotent per (session,
          // epoch, turn) at the transport level — re-registering an existing
          // entry never opens a second connection — but
          // `attachDurableChatTurnToSession` itself is NOT: it
          // unconditionally adds a fresh registry listener and a fresh
          // sidebar-cover query-cache subscriber every call, so invoking it
          // twice for the same turn double-fires every one of those side
          // effects. This is the check that keeps the sender's own tab (and
          // this poll's own overlapping ticks) from ever attaching twice.
          if (chatStoreRef.current.getState().sessionStates[pollSessionId]?.durableTurn) return
          attachFromCurrent(current)
        })
        .catch((error: unknown) => {
          consecutiveFailures += 1
          // Log once per failure STREAK, not once per tick — a session
          // stuck behind e.g. 403 `durable_turns_disabled` would otherwise
          // warn every `DURABLE_TURN_POLL_INTERVAL_MS` for as long as the
          // tab stays open (issue: durable activity-latency review, finding
          // 4).
          if (consecutiveFailures === 1) {
            logger.warn("Durable chat turn poll failed", error as Error)
          }
          if (isPermanentPollFailure(error)) {
            permanentGiveUp = true
          }
        })
        .finally(() => {
          requestInFlight = false
          if (cancelled) return
          if (permanentGiveUp) {
            // Give up: retrying can never resolve a 403/404 (see
            // `isPermanentPollFailure`), so no further requests for the
            // remainder of THIS mount. Only a genuine session change (a
            // fresh run of this effect) resumes polling. Unlike the old
            // streak-based cutoff (issue #2513 finding 18), this never fires
            // for an ordinary transient failure no matter how long it runs.
            logger.warn("Durable chat turn poll giving up: permanent failure", {
              sessionId: pollSessionId,
            })
            return
          }
          // Shared with the WS reconnect and registry recovery backoffs
          // (adversarial review round 3, reuse finding) -- this used to
          // reimplement the same capped-exponential formula inline. `base *
          // 2^0 === base`, so `computeBackoffDelayMs(consecutiveFailures,
          // ...)` alone reproduces the old ternary's un-backed-off case too.
          const delay = computeBackoffDelayMs(consecutiveFailures, {
            base: DURABLE_TURN_POLL_INTERVAL_MS,
            max: DURABLE_TURN_POLL_MAX_BACKOFF_MS,
          })
          timeoutId = window.setTimeout(tick, delay)
        })
    }

    // Skip straight to the next tick instead of waiting out the remaining
    // backoff: a transient failure's delay was chosen assuming the backend
    // was still unreachable, which regaining connectivity/focus falsifies.
    // Likewise after ticks skipped while hidden (#3354). No-ops while healthy
    // and visible, or already given up (issue #2513 finding 18).
    const handleReconnectSignal = (): void => {
      if (cancelled || permanentGiveUp || requestInFlight) return
      if (consecutiveFailures === 0 && !skippedWhileHidden) return
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
      tick()
    }
    window.addEventListener("online", handleReconnectSignal)
    const unsubscribeFocus = focusManager.subscribe((focused) => {
      if (focused) handleReconnectSignal()
    })

    // No immediate first tick — see this effect's own leading comment above.
    timeoutId = window.setTimeout(tick, DURABLE_TURN_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
      window.removeEventListener("online", handleReconnectSignal)
      unsubscribeFocus()
    }
  }, [enabled, sessionId, isAttached])

  // Detach the UI mirror (never cancels the turn) strictly when the target
  // session id itself changes or the hook unmounts — independent of the
  // hydration/poll effects' own re-run cadence above.
  useEffect(() => {
    return () => {
      if (hydratedSessionRef.current === sessionId) {
        hydratedSessionRef.current = null
      }
      detachRef.current?.()
      detachRef.current = null
    }
  }, [sessionId])
}
